import React, { forwardRef } from 'react';
import styles from './Typography.module.css';

/* ── Helpers ── */

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

/* ── Shared types ── */

export type TypographyColor = 'default' | 'secondary' | 'muted' | 'primary' | 'danger' | 'success';

/* ─────────────────────────────────────────
   Heading
   ───────────────────────────────────────── */

export type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;

export interface HeadingProps extends React.HTMLAttributes<HTMLHeadingElement> {
  level?: HeadingLevel;
  /** Render a different HTML tag while keeping the visual size of `level` */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span' | 'div';
  color?: TypographyColor;
  truncate?: boolean;
}

const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ level = 1, as, color = 'default', truncate, className, children, ...rest }, ref) => {
    const Tag = (as ?? `h${level}`) as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cx(
          styles.heading,
          styles[`h${level}`],
          styles[color],
          truncate && styles.truncate,
          className,
        )}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

Heading.displayName = 'Typography.Heading';

/* ─────────────────────────────────────────
   Text
   ───────────────────────────────────────── */

export type TextSize = 'xl' | 'lg' | 'md' | 'sm' | 'xs';
export type TextWeight = 'regular' | 'medium' | 'semibold';

export interface TextProps extends React.HTMLAttributes<HTMLElement> {
  size?: TextSize;
  weight?: TextWeight;
  color?: TypographyColor;
  /** Render a specific HTML tag (default: span) */
  as?: 'p' | 'span' | 'div' | 'label' | 'strong' | 'em' | 'small' | 'li';
  truncate?: boolean;
  /** Renders as an uppercase tracking label */
  label?: boolean;
}

const Text = forwardRef<HTMLElement, TextProps>(
  ({ size = 'md', weight = 'regular', color = 'default', as = 'span', truncate, label, className, children, ...rest }, ref) => {
    const Tag = as as React.ElementType;
    return (
      <Tag
        ref={ref}
        className={cx(
          styles.text,
          styles[`size-${size}`],
          styles[`weight-${weight}`],
          styles[color],
          truncate && styles.truncate,
          label && styles.label,
          className,
        )}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

Text.displayName = 'Typography.Text';

/* ─────────────────────────────────────────
   Compound export
   ───────────────────────────────────────── */

export const Typography = { Heading, Text };
